// studio_small_09 at 135° and exposure 1.25 beat softbox on the green stage: shoulder
// highlights read as glass and the bottle edge separates from the backdrop (TAS-369).
import bottleEnvironmentUrl from '../../../../packages/bottle-viewer/assets/studio_small_09_1k.hdr?url';
import { bottleModelFor } from './bottleModels';
import { recordFetchSample } from '@tasted/utils/netTier';

let viewerModule;
let loadedViewerModule = null;

export function loadRevealBottle3D() {
  // Forget a failed download so a later wine can retry once the network is back.
  viewerModule ??= import('../components/reveal/RevealBottle3D').then((module) => {
    loadedViewerModule = module;
    return module;
  }, (error) => {
    viewerModule = undefined;
    throw error;
  });
  return viewerModule;
}

/**
 * The 3D chunk if it has already arrived, else null. A screen that only shows
 * a bottle once the reveal has one (the tasting notes) can render from it
 * straight away, rather than suspending for a chunk that is already here.
 */
export function loadedRevealBottle3D() {
  return loadedViewerModule;
}

/**
 * GET the published GLB and packaged HDR as blobs, without creating a renderer.
 * `ready` resolves with { src, hdr } object URLs for the viewer, or rejects on
 * an import/network failure. The caller owns cancel(), including after ready.
 */
export function preloadBottle(wineId) {
  const modelUrl = bottleModelFor(wineId);
  if (!modelUrl) return null;

  const controller = new AbortController();
  const objectUrls = new Set();
  const cancel = () => {
    controller.abort();
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.clear();
  };
  const checkActive = () => {
    if (controller.signal.aborted) throw new DOMException('Bottle preload cancelled', 'AbortError');
  };
  const fetchResource = async (url) => {
    const startedAt = performance.now();
    const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
    if (!response.ok) throw new Error(`Bottle asset request failed (${response.status})`);
    const blob = await response.blob();
    // The heaviest thing the game fetches, timed by us (TAS-398). The reveal
    // fetches the same model and lighting map again for every wine, mostly
    // from the HTTP cache, and a disk read must not count as the network
    // (TAS-526). The reading is filed a task later because the browser can
    // file the fetch's timing entry, which says where it came from, after
    // blob() resolves.
    const bytes = blob.size;
    const ms = performance.now() - startedAt;
    setTimeout(() => recordFetchSample({ url, bytes, ms, startedAt }), 0);
    checkActive();
    const objectUrl = URL.createObjectURL(blob);
    objectUrls.add(objectUrl);
    return objectUrl;
  };

  const ready = Promise.all([
    loadRevealBottle3D(),
    fetchResource(modelUrl),
    fetchResource(bottleEnvironmentUrl),
  ]).then(([, src, hdr]) => {
    checkActive();
    return { src, hdr };
  }).catch((error) => {
    cancel();
    throw error;
  });
  // A page can unmount before subscribing to the result.
  void ready.catch(() => {});

  return { modelUrl, ready, cancel };
}

// ── One viewer, two surfaces ────────────────────────────────────────────────
// The tasting-notes sheet shows the reveal's bottle behind its pages. It used
// to build a second viewer for it: a second renderer, the model parsed again,
// the lighting decoded and baked again, every shader compiled again — the
// wait between "See tasting notes" and the bottle (Mel, 2026-10-29). Now the
// reveal offers its viewer here once its bottle is up, and the sheet borrows
// that one, looked up by wine. Only one reveal is on screen at a time; the map
// is keyed by wine so a sheet never borrows another wine's bottle.

const revealViewers = new Map();
const revealViewerListeners = new Set();

function notifyRevealViewers() {
  for (const listener of [...revealViewerListeners]) listener();
}

export function subscribeRevealViewers(listener) {
  revealViewerListeners.add(listener);
  return () => { revealViewerListeners.delete(listener); };
}

/** The reveal viewer on offer for this wine, or null. Stable until it changes. */
export function revealViewerFor(wineId) {
  return (wineId && revealViewers.get(wineId)) || null;
}

/** True while this viewer is lent out: its owner must leave the camera alone. */
export function isRevealViewerLent(viewer) {
  if (!viewer) return false;
  for (const entry of revealViewers.values()) {
    if (entry.viewer === viewer && entry.lent) return true;
  }
  return false;
}

/**
 * Offer the reveal's viewer, with its bottle already drawn, to the tasting
 * notes. Returns withdraw(), for when that viewer goes away. A viewer
 * withdrawn while it is lent stays with the borrower until it is given back,
 * so the bottle behind the notes does not vanish because the reveal did.
 */
export function shareRevealViewer(wineId, viewer) {
  const entry = { wineId, viewer, owned: true, lent: false };
  revealViewers.set(wineId, entry);
  notifyRevealViewers();
  return () => {
    if (!entry.owned) return;
    entry.owned = false;
    if (!entry.lent && revealViewers.get(wineId) === entry) revealViewers.delete(wineId);
    notifyRevealViewers();
  };
}

/**
 * Show a shared viewer inside `host`, in the borrower's presentation and
 * opening pose, and return giveBack(). The canvas moves; nothing is rebuilt.
 * Giving back puts the owner's presentation and camera back and moves the
 * canvas home. If the owner withdrew meanwhile there is no home to go to, and
 * giving back releases the viewer instead, which disposes it (once).
 */
export function lendRevealViewer(entry, host, { presentation = {}, pose = null } = {}) {
  const { viewer } = entry;
  const release = viewer.retain();
  entry.lent = true;
  const resting = viewer.getPose();
  // The owner pauses its idle turn while lent (isRevealViewerLent); stop it
  // here too, so the borrowed bottle cannot drift before the owner hears.
  viewer.setAutoRotate(false);
  const previous = viewer.setPresentation(presentation);
  if (pose) void viewer.animateTo({ ...pose, duration: 0 });
  const home = viewer.attach(host);
  notifyRevealViewers();
  let returned = false;
  return () => {
    if (returned) return;
    returned = true;
    entry.lent = false;
    if (entry.owned) {
      viewer.setPresentation(previous);
      if (resting) void viewer.animateTo({ ...resting, duration: 0 });
      viewer.attach(home);
    } else if (revealViewers.get(entry.wineId) === entry) {
      revealViewers.delete(entry.wineId);
    }
    release();
    notifyRevealViewers();
  };
}
